// AreaMultiSelect — the shared "preferred areas" picker.
//
// Layout (top → bottom):
//   1. Quick-pick toggle chips: the coarse regions + a CBD shortcut +
//      "Anywhere". One tap selects/deselects. "Anywhere" is mutually
//      exclusive with everything else.
//   2. A search box for the 28 URA districts — type a town ("tampines"),
//      a district number ("18" / "d18"), or a region name. Matches appear
//      in a dropdown; clicking one adds it.
//   3. Removable chips for the selected districts (regions/CBD/Anywhere are
//      reflected by the highlighted quick-pick chips above, so they're not
//      duplicated here).
//
// Tokens written into `value`: "central"/"east"/… , "cbd", "any", and
// "d01".."d28". Every token resolves in the backend's AreasToDistricts.
// Districts are always shown by their town label — never a "D18" code.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ANY_TOKEN,
  QUICK_PICK_TOKENS,
  areaLabel,
  isDistrictToken,
  searchableAreaOptions,
  type Translate,
} from "@/lib/areaOptions";

export function AreaMultiSelect({
  value,
  onChange,
}: {
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const { t } = useTranslation();
  // react-i18next's t isn't structurally assignable to Translate under the
  // project's strict TS config (same reason WsTCtx casts `t as TFn`); cast
  // once and pass the typed handle to the label/search helpers.
  const tr = t as Translate;
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const has = (token: string) => value.includes(token);

  const toggle = (token: string) => {
    if (has(token)) {
      onChange(value.filter((x) => x !== token));
      return;
    }
    // "Anywhere" clears every other pick; any specific pick clears "Anywhere".
    if (token === ANY_TOKEN) {
      onChange([ANY_TOKEN]);
      return;
    }
    onChange([...value.filter((x) => x !== ANY_TOKEN), token]);
  };

  const q = query.trim().toLowerCase();
  const matches = q
    ? searchableAreaOptions(tr)
        .filter((o) => !has(o.token) && o.search.includes(q))
        .slice(0, 8)
    : [];

  const selectedDistricts = value.filter(isDistrictToken);

  const selectMatch = (token: string) => {
    toggle(token);
    setQuery("");
  };

  return (
    <div>
      {/* 1. Quick-pick regions + CBD + Anywhere */}
      <div className="flex flex-wrap gap-1.5">
        {QUICK_PICK_TOKENS.map((tok) => {
          const active = has(tok);
          return (
            <button
              key={tok}
              type="button"
              onClick={() => toggle(tok)}
              aria-pressed={active}
              className={
                "rounded-full border px-2.5 py-1 text-[12px] transition-colors " +
                (active
                  ? "border-emerald-500 bg-emerald-50 text-emerald-900 font-medium"
                  : "border-border bg-surface-2 text-text-sec hover:bg-surface-3")
              }
            >
              {areaLabel(tok, tr)}
            </button>
          );
        })}
      </div>

      {/* 2. District search */}
      <div className="relative mt-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && matches.length > 0) {
              e.preventDefault();
              selectMatch(matches[0].token);
            } else if (e.key === "Escape") {
              setQuery("");
            }
          }}
          placeholder={t("auth.prefs.areaSearchPlaceholder")}
          className="w-full rounded-md border border-border bg-surface-2 px-2.5 py-1.5 text-[12px] text-text placeholder:text-text-muted focus:border-emerald-500 focus:outline-none"
        />
        {focused && q && (
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-border bg-surface shadow-lg">
            {matches.length === 0 ? (
              <div className="px-3 py-2 text-[12px] text-text-muted">
                {t("auth.prefs.areaNoMatch")}
              </div>
            ) : (
              matches.map((o) => (
                <button
                  key={o.token}
                  type="button"
                  // onMouseDown (not onClick) so selection fires before the
                  // input's onBlur tears the dropdown down.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectMatch(o.token);
                  }}
                  className="block w-full px-3 py-1.5 text-left text-[12px] text-text-sec hover:bg-surface-2"
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* 3. Selected districts (removable) */}
      {selectedDistricts.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selectedDistricts.map((tok) => (
            <span
              key={tok}
              className="inline-flex items-center gap-1 rounded-full border border-emerald-500 bg-emerald-50 px-2.5 py-1 text-[12px] font-medium text-emerald-900"
            >
              {areaLabel(tok, tr)}
              <button
                type="button"
                onClick={() => toggle(tok)}
                aria-label={t("common.remove", { defaultValue: "Remove" })}
                className="text-emerald-700 hover:text-emerald-900"
                style={{ lineHeight: 1, fontSize: 14 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
